import React, { useState, useEffect } from 'react';
import { Book, Users, Home, Scroll, MapPin, Mountain, Eye } from 'lucide-react';

/**
 * SpiralORG.org Landing Page - Version A: Spiral Notebook Vibe
 * Old-fashioned, warm, handwritten aesthetic with paper textures
 */
const SpiralOrgLandingNotebook = ({ onEnterProjects, onCreateManifesto, onEnterReInn }) => {
  const [spiralRotation, setSpiralRotation] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setSpiralRotation(prev => (prev + 0.2) % 360);
    }, 50);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-[#f4e8d8] relative overflow-hidden font-serif">
      {/* Grant Spiral Background - 50% slower */}
      <div 
        className="absolute inset-0 opacity-20 pointer-events-none"
        style={{
          background: `radial-gradient(circle at center, transparent 0%, rgba(92, 45, 115, 0.05) 100%)`,
          transform: `rotate(${spiralRotation}deg)`,
          transition: 'transform 0.05s linear'
        }}
      >
        <svg className="w-full h-full" viewBox="0 0 1000 1000">
          <defs>
            <linearGradient id="spiralGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" style={{ stopColor: '#5C2D73', stopOpacity: 0.3 }} />
              <stop offset="100%" style={{ stopColor: '#E6C685', stopOpacity: 0.3 }} />
            </linearGradient>
          </defs>
          {[...Array(8)].map((_, i) => (
            <path
              key={i}
              d={`M 500,500 Q ${500 + (i + 1) * 60},${500 - (i + 1) * 60} ${500 + (i + 1) * 60},${500 + (i + 1) * 60}`}
              fill="none"
              stroke="url(#spiralGradient)"
              strokeWidth="2"
              opacity={0.6 - i * 0.07}
            />
          ))}
        </svg>
      </div>

      {/* Spiral Binding on Left */}
      <div className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-[#8B7355] to-transparent flex flex-col items-center py-8 gap-6">
        {[...Array(20)].map((_, i) => (
          <div key={i} className="w-6 h-6 rounded-full border-2 border-[#5C2D73] bg-[#E6C685] shadow-md"></div>
        ))}
      </div>

      {/* Paper Texture Overlay */}
      <div 
        className="absolute inset-0 opacity-10 pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' /%3E%3C/filter%3E%3Crect width='100' height='100' filter='url(%23noise)' opacity='0.3'/%3E%3C/svg%3E")`,
        }}
      ></div>

      {/* Main Content with Torn Page Edge */}
      <div className="relative ml-12 min-h-screen">
        {/* Torn Edge Effect */}
        <div 
          className="absolute left-0 top-0 bottom-0 w-4"
          style={{
            background: `repeating-linear-gradient(180deg, #f4e8d8 0px, #f4e8d8 10px, transparent 10px, transparent 20px)`,
            filter: 'drop-shadow(-2px 0 2px rgba(0,0,0,0.1))'
          }}
        ></div>

        <div className="max-w-5xl mx-auto px-8 py-16">
          {/* Header with Owl */}
          <div className="relative mb-16">
            <div className="absolute inset-0">
              <img 
                src="/owl-hero.jpeg" 
                alt="Mystical Owl" 
                className="w-full h-[400px] object-cover rounded-lg opacity-40"
                style={{ filter: 'sepia(0.3) contrast(1.1)' }}
              />
            </div>
            
            <div className="relative z-10 text-center pt-32 pb-16">
              <h1 
                className="text-7xl mb-6 tracking-wide"
                style={{ 
                  fontFamily: "'Brush Script MT', cursive",
                  color: '#1E2A38',
                  textShadow: '2px 2px 4px rgba(255,255,255,0.8)'
                }}
              >
                SpiralORG.org
              </h1>
              <p 
                className="text-2xl leading-relaxed max-w-2xl mx-auto"
                style={{ 
                  fontFamily: "'Lucida Handwriting', cursive",
                  color: '#5C2D73'
                }}
              >
                A guild to build belonging<br/>
                with logistics and song
              </p>
            </div>
          </div>

          {/* Handwritten Description */}
          <div className="bg-[#fffef7] rounded-lg p-8 mb-12 border-2 border-[#CED3CC] shadow-lg transform rotate-[-0.5deg]">
            <p 
              className="text-xl leading-relaxed text-[#1E2A38]"
              style={{ fontFamily: "'Bradley Hand', cursive" }}
            >
              We are a private planetary federation of families, seeking bliss, tech, and mastery, 
              to help us get things done! By invitation only.
            </p>
            <div className="mt-4 text-sm text-[#5C2D73] italic">
              Brought to you by Planetary Fed
            </div>
          </div>

          {/* Two Main Entry Paths - Handwritten Cards */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {/* Enter Projects */}
            <button
              onClick={onEnterProjects}
              className="group bg-[#fffef7] rounded-lg p-8 border-2 border-[#E6C685] hover:border-[#5C2D73] 
                transition-all duration-300 hover:shadow-2xl hover:scale-105 transform rotate-[1deg] hover:rotate-0"
            >
              <div className="flex flex-col items-center text-center">
                <Book className="w-16 h-16 mb-4 text-[#5C2D73] group-hover:text-[#E6C685] transition-colors" />
                <h3 
                  className="text-3xl mb-4"
                  style={{ fontFamily: "'Brush Script MT', cursive", color: '#1E2A38' }}
                >
                  Enter Projects
                </h3>
                <p 
                  className="text-lg leading-relaxed"
                  style={{ fontFamily: "'Bradley Hand', cursive", color: '#5C2D73' }}
                >
                  Jump straight into your work. Manage families, enterprises, and sacred collaborations.
                </p>
              </div>
            </button>

            {/* Create Manifesto */}
            <button
              onClick={onCreateManifesto}
              className="group bg-[#fffef7] rounded-lg p-8 border-2 border-[#E6C685] hover:border-[#5C2D73] 
                transition-all duration-300 hover:shadow-2xl hover:scale-105 transform rotate-[-1deg] hover:rotate-0"
            >
              <div className="flex flex-col items-center text-center">
                <Scroll className="w-16 h-16 mb-4 text-[#5C2D73] group-hover:text-[#E6C685] transition-colors" />
                <h3 
                  className="text-3xl mb-4"
                  style={{ fontFamily: "'Brush Script MT', cursive", color: '#1E2A38' }}
                >
                  Create My Manifesto
                </h3>
                <p 
                  className="text-lg leading-relaxed"
                  style={{ fontFamily: "'Bradley Hand', cursive", color: '#5C2D73' }}
                >
                  Begin your journey. Guided by M.ai.ry to discover your purpose and connect with your people.
                </p>
              </div>
            </button>
          </div>

          {/* Rites of Presence Section */}
          <div className="bg-[#fffef7] rounded-lg p-8 mb-12 border-2 border-[#CED3CC] shadow-lg transform rotate-[0.5deg]">
            <h2 
              className="text-4xl mb-6 text-center"
              style={{ fontFamily: "'Brush Script MT', cursive", color: '#5C2D73' }}
            >
              Rites of Presence
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex items-start gap-4">
                <MapPin className="w-8 h-8 text-[#5C2D73] flex-shrink-0 mt-1" />
                <div>
                  <h3 
                    className="text-2xl mb-2"
                    style={{ fontFamily: "'Bradley Hand', cursive", color: '#1E2A38' }}
                  >
                    Driftless Region Tours
                  </h3>
                  <p className="text-[#5C2D73]">
                    Join Yusuf for guided explorations through the magical Driftless region 
                    of the United States. Ancient valleys, sacred springs, family homesteads.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Mountain className="w-8 h-8 text-[#5C2D73] flex-shrink-0 mt-1" />
                <div>
                  <h3 
                    className="text-2xl mb-2"
                    style={{ fontFamily: "'Bradley Hand', cursive", color: '#1E2A38' }}
                  >
                    Heart of Morocco
                  </h3>
                  <p className="text-[#5C2D73]">
                    Experience the depth of Moroccan culture with Yusuf. 
                    Markets, mountains, music, and mystical traditions.
                  </p>
                </div>
              </div>
            </div>
            <div className="text-center mt-6">
              <a 
                href="https://re-inn.com" 
                className="inline-block px-8 py-3 bg-[#5C2D73] text-[#E6C685] rounded-lg 
                  hover:bg-[#E6C685] hover:text-[#5C2D73] transition-all duration-300 text-lg font-bold"
              >
                Explore via Re-Inn.com
              </a>
            </div>
          </div>

          {/* Quick Links Grid */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {[
              { icon: Home, title: 're-Inn Stays', desc: 'Sacred retreats & family reunions' },
              { icon: Users, title: 'Family Federation', desc: 'AirBNB for Care' },
              { icon: Eye, title: 'Secret History Game', desc: 'Discover the timeline' }
            ].map((item, i) => (
              <div 
                key={i}
                className="bg-[#fffef7] rounded-lg p-6 border border-[#CED3CC] hover:shadow-lg 
                  transition-all cursor-pointer transform hover:scale-105"
                style={{ transform: `rotate(${i % 2 === 0 ? '0.5' : '-0.5'}deg)` }}
              >
                <item.icon className="w-12 h-12 mb-3 text-[#5C2D73]" />
                <h4 
                  className="text-xl mb-2"
                  style={{ fontFamily: "'Bradley Hand', cursive", color: '#1E2A38' }}
                >
                  {item.title}
                </h4>
                <p className="text-sm text-[#5C2D73]">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Footer Links */}
          <div className="text-center space-y-4 py-8 border-t-2 border-[#CED3CC]">
            <div className="flex justify-center gap-8 flex-wrap">
              {[
                { name: 'seedypeeps.com', url: 'https://seedypeeps.com' },
                { name: 'planetaryfed.com', url: 'https://planetaryfed.com' },
                { name: 'trustwoven.com', url: 'https://trustwoven.com' },
                { name: 're-inn.com', url: 'https://re-inn.com' }
              ].map((link, i) => (
                <a
                  key={i}
                  href={link.url}
                  className="text-[#5C2D73] hover:text-[#E6C685] transition-colors text-lg underline"
                  style={{ fontFamily: "'Bradley Hand', cursive" }}
                >
                  {link.name}
                </a>
              ))}
            </div>
            <div className="text-[#5C2D73] text-sm">
              Sacred Retreats • Family Archives • Remedy Council
            </div>
            <button 
              className="mt-6 px-6 py-2 border-2 border-[#5C2D73] text-[#5C2D73] rounded-lg 
                hover:bg-[#5C2D73] hover:text-[#E6C685] transition-all"
            >
              Join Waitlist
            </button>
            <div className="mt-4">
              <a href="#" className="text-sm text-[#1E2A38] underline">
                Click here if you have a code...
              </a>
            </div>

            {/* Version Toggle */}
            <div className="mt-8 pt-6 border-t border-[#CED3CC]">
              <button
                onClick={() => window.location.href = '?version=mystical'}
                className="px-6 py-2 bg-gradient-to-r from-[#5C2D73] to-[#E6C685] text-white rounded-lg 
                  hover:shadow-lg hover:scale-105 transition-all text-sm"
              >
                ✨ Switch to Mystical Version
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpiralOrgLandingNotebook;
